import React, { useState, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  MapPin, Home, Bed, Bath, Square, DollarSign, 
  Edit, Trash2, Check, XCircle, Upload, X, Image as ImageIcon,
  Eye, Heart, TrendingUp, User, Building, ChevronLeft, ChevronRight,
  Sparkles, Coffee, Users as UsersIcon, BookOpen, Leaf, Sun, Moon, Star, PawPrint
} from "lucide-react";
import { VibeTag, getAllVibeTagsArray } from "@/components/VibeTag";
import { PropertyQuestionnaire } from "@/components/PropertyQuestionnaire";
import { PsychographicGauge, PsychographicBadge } from "@/components/PsychographicGauge";
import { PhotoManager } from "@/components/PhotoManager";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Image Gallery Component with navigation
const ImageGallery = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (!images || images.length === 0) {
    return (
      <div className="w-full h-64 bg-slate-100 rounded-xl flex items-center justify-center">
        <ImageIcon className="w-12 h-12 text-slate-300" />
      </div>
    );
  }
  
  return (
    <div className="relative">
      <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
        <img 
          src={images[currentIndex]} 
          alt={`${title} - ${currentIndex + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      
      {images.length > 1 && (
        <>
          <button 
            onClick={() => setCurrentIndex(prev => prev > 0 ? prev - 1 : images.length - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setCurrentIndex(prev => prev < images.length - 1 ? prev + 1 : 0)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, idx) => (
              <button 
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? 'bg-white' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </>
      )}
      
      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {images.map((img, idx) => (
            <button 
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 ${idx === currentIndex ? 'border-primary' : 'border-transparent'}`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Features Grid Component
const FeaturesGrid = ({ features }) => {
  if (!features) return null;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {features.bedrooms !== undefined && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <Bed className="w-5 h-5 text-primary" />
          <div>
            <p className="text-lg font-semibold">{features.bedrooms}</p>
            <p className="text-xs text-slate-500">Recámaras</p>
          </div>
        </div>
      )}
      {features.bathrooms !== undefined && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <Bath className="w-5 h-5 text-blue-500" />
          <div>
            <p className="text-lg font-semibold">{features.bathrooms}</p>
            <p className="text-xs text-slate-500">Baños</p>
          </div>
        </div>
      )}
      {/* Land Area - supports both land_area_m2 and legacy surface_area_m2/area */}
      {(features.land_area_m2 || features.surface_area_m2 || features.area) && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <Square className="w-5 h-5 text-green-500" />
          <div>
            <p className="text-lg font-semibold">{features.land_area_m2 || features.surface_area_m2 || features.area} m²</p>
            <p className="text-xs text-slate-500">Terreno</p>
          </div>
        </div>
      )}
      {/* Construction Area */}
      {features.construction_area_m2 !== undefined && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <Home className="w-5 h-5 text-indigo-500" />
          <div>
            <p className="text-lg font-semibold">{features.construction_area_m2} m²</p>
            <p className="text-xs text-slate-500">Construido</p>
          </div>
        </div>
      )}
      {features.price !== undefined && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
          <DollarSign className="w-5 h-5 text-amber-500" />
          <div>
            <p className="text-lg font-semibold">${(features.price / 1000000).toFixed(1)}M</p>
            <p className="text-xs text-slate-500">MXN</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Image Upload Section for Admin/Agent
const ImageUploadSection = ({ images, propertyId, onImagesUpdated, canEdit }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleImageUpload = async (file) => {
    if (!file || !propertyId) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 10MB.");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Try agent endpoint first, then admin
      try {
        await axios.post(`${API}/agent/properties/${propertyId}/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (e) {
        await axios.post(`${API}/admin/properties/${propertyId}/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success("Imagen subida exitosamente");
      if (onImagesUpdated) onImagesUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };
  
  const handleDeleteImage = async (imageIndex) => {
    try {
      // Try agent endpoint first, then admin
      try {
        await axios.delete(`${API}/agent/properties/${propertyId}/images/${imageIndex}`);
      } catch (e) {
        await axios.delete(`${API}/admin/properties/${propertyId}/images/${imageIndex}`);
      }
      toast.success("Imagen eliminada");
      if (onImagesUpdated) onImagesUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar imagen");
    }
  };
  
  if (!canEdit) return null;
  
  return (
    <Card className="mt-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ImageIcon className="w-4 h-4" /> Gestionar Imágenes
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-4">
          {images?.map((img, idx) => (
            <div key={idx} className="relative group">
              <img src={img} alt={`Image ${idx + 1}`} className="w-20 h-16 object-cover rounded-lg" />
              <button 
                onClick={() => handleDeleteImage(idx)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => e.target.files?.[0] && handleImageUpload(e.target.files[0])}
          accept="image/*"
          className="hidden"
        />
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          <Upload className="w-4 h-4 mr-2" />
          {uploading ? "Subiendo..." : "Agregar Imagen"}
        </Button>
      </CardContent>
    </Card>
  );
};

// Edit Property Form
const EditPropertyForm = ({ property, onSave, onCancel, onImagesUpdated, initialTab = "info" }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [formData, setFormData] = useState({
    title: property?.title || "",
    story: property?.story || "",
    municipality: property?.municipality || "",
    features: property?.features || { bedrooms: 2, bathrooms: 2, land_area_m2: 100, construction_area_m2: 80, price: 3000000 }
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handlePsychographicComplete = async (psychographicAttributes) => {
    setSaving(true);
    try {
      await onSave({ psychographic_attributes: psychographicAttributes });
      toast.success("Perfil psicográfico actualizado. Los vibe tags se han regenerado automáticamente.");
    } finally {
      setSaving(false);
    }
  };

  // Pre-compute initial answers for the questionnaire from property's psychographic_attributes
  const initialAnswers = React.useMemo(() => {
    const attrs = property?.psychographic_attributes || {};
    const flat = {};
    ["emotional", "technical", "urban", "financial"].forEach(cat => {
      Object.entries(attrs[cat] || {}).forEach(([qid, val]) => {
        flat[qid] = val;
      });
    });
    return flat;
  }, [property?.psychographic_attributes]);

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full" data-testid="edit-property-tabs">
          <TabsTrigger value="info" data-testid="tab-info">
            <Edit className="w-4 h-4 mr-1" /> Información
          </TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">
            <ImageIcon className="w-4 h-4 mr-1" /> Fotografías
            {property?.images?.length > 0 && (
              <Badge variant="secondary" className="ml-1">{property.images.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="psychographic" data-testid="tab-psychographic">
            <Sparkles className="w-4 h-4 mr-1" /> Perfil (4 olas)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4 pt-4">
          <div>
            <label className="text-sm font-medium text-slate-600">Título</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Título de la propiedad"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Descripción / Historia</label>
            <Textarea
              value={formData.story}
              onChange={(e) => setFormData(prev => ({ ...prev, story: e.target.value }))}
              placeholder="Describe la propiedad..."
              rows={4}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Municipio / Zona</label>
            <Input
              value={formData.municipality}
              onChange={(e) => setFormData(prev => ({ ...prev, municipality: e.target.value }))}
              placeholder="Ej: Roma Norte, Polanco..."
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Recámaras</label>
              <Input type="number" value={formData.features.bedrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, features: { ...prev.features, bedrooms: parseInt(e.target.value) || 0 } }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Baños</label>
              <Input type="number" value={formData.features.bathrooms}
                onChange={(e) => setFormData(prev => ({ ...prev, features: { ...prev.features, bathrooms: parseFloat(e.target.value) || 0 } }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Terreno (m²)</label>
              <Input type="number" value={formData.features.land_area_m2 || formData.features.area || 0}
                onChange={(e) => setFormData(prev => ({ ...prev, features: { ...prev.features, land_area_m2: parseInt(e.target.value) || 0 } }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Construido (m²)</label>
              <Input type="number" value={formData.features.construction_area_m2 || 0}
                onChange={(e) => setFormData(prev => ({ ...prev, features: { ...prev.features, construction_area_m2: parseInt(e.target.value) || 0 } }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Precio (MXN)</label>
              <Input type="number" value={formData.features.price}
                onChange={(e) => setFormData(prev => ({ ...prev, features: { ...prev.features, price: parseInt(e.target.value) || 0 } }))} />
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-[#b8c100]" data-testid="save-property-info">
              {saving ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="photos" className="pt-4">
          <PhotoManager
            propertyId={property.id}
            images={property.images || []}
            onChange={(newImages) => {
              // mutate parent on every change so the modal preview stays in sync
              if (onImagesUpdated) onImagesUpdated();
              // local mutation for snappy UI
              if (property) property.images = newImages;
            }}
          />
          <div className="flex gap-2 justify-end pt-4 border-t mt-4">
            <Button variant="outline" onClick={onCancel}>Cerrar</Button>
          </div>
        </TabsContent>

        <TabsContent value="psychographic" className="pt-4">
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <strong>Perfil psicográfico de la propiedad.</strong> Completa o actualiza las 4 olas (Emocional,
            Arquitectura, Entorno, Financiero) con los 52 factores. Al guardar, se regenerarán los vibe-tags
            y mejorará el matching con compradores compatibles — sube tu score del RIC.
          </div>
          <PropertyQuestionnaire
            mode="agent"
            propertyId={property.id}
            initialAnswers={initialAnswers}
            onComplete={handlePsychographicComplete}
            onSkipAll={onCancel}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Main Property Detail Modal Component
export const PropertyDetailModal = ({ 
  open, 
  onOpenChange, 
  property, 
  owner, 
  agent, 
  statistics,
  canEdit = false,
  canDelete = false,
  canApprove = false,
  onApprove,
  onReject,
  onDelete,
  onUpdate,
  showMatchScore = false,
  matchScore = null,
  matchBreakdown = null,
  initialEditMode = false,
  initialEditTab = "info"
}) => {
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [editTab, setEditTab] = useState(initialEditTab);
  const [refreshKey, setRefreshKey] = useState(0);

  // Sync with external control (e.g., when RIC/MatchGap opens the modal in edit mode)
  React.useEffect(() => {
    if (open) {
      setIsEditing(initialEditMode);
      setEditTab(initialEditTab);
    }
  }, [open, initialEditMode, initialEditTab]);
  
  const handleSaveEdit = async (formData) => {
    try {
      // Try agent endpoint first, then admin
      try {
        await axios.put(`${API}/agent/properties/${property.id}`, formData);
      } catch (e) {
        await axios.put(`${API}/admin/properties/${property.id}`, formData);
      }
      toast.success("Propiedad actualizada exitosamente");
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar propiedad");
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de eliminar esta propiedad? Esta acción no se puede deshacer.")) return;
    
    try {
      if (onDelete) {
        await onDelete(property.id);
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar propiedad");
    }
  };
  
  if (!property) return null;
  
  const statusColors = {
    active: "bg-green-100 text-green-700",
    pending_approval: "bg-amber-100 text-amber-700",
    inactive: "bg-slate-100 text-slate-600",
    rejected: "bg-red-100 text-red-700"
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="property-detail-modal" aria-describedby="property-detail-description">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>{isEditing ? "Editar Propiedad" : property.title}</span>
            {!isEditing && (
              <Badge className={statusColors[property.status] || "bg-slate-100"}>
                {property.status === "active" ? "Activa" : 
                 property.status === "pending_approval" ? "Pendiente" :
                 property.status === "rejected" ? "Rechazada" : property.status}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <p id="property-detail-description" className="sr-only">
          Detalles completos de la propiedad {property.title}
        </p>
        
        <ScrollArea className="flex-1 pr-4">
          {isEditing ? (
            <EditPropertyForm 
              property={property} 
              onSave={handleSaveEdit}
              onCancel={() => setIsEditing(false)}
              onImagesUpdated={() => { setRefreshKey(prev => prev + 1); if (onUpdate) onUpdate(); }}
              initialTab={editTab}
            />
          ) : (
            <div className="space-y-6 py-4">
              {/* Image Gallery */}
              <ImageGallery images={property.images} title={property.title} />

              {/* Psychographic Completion Gauge - visible for agent/owner/admin */}
              {canEdit && (
                <PsychographicGauge
                  psychographicAttributes={property.psychographic_attributes}
                  onEditWave={() => { setIsEditing(true); setEditTab("psychographic"); }}
                />
              )}
              
              {/* Match Score (for buyers) */}
              {showMatchScore && matchScore !== null && (
                <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-600">Tu Compatibilidad</p>
                        <p className="text-3xl font-bold text-primary">{matchScore}%</p>
                      </div>
                      <Heart className="w-10 h-10 text-primary" />
                    </div>
                    {matchBreakdown && (
                      <div className="grid grid-cols-4 gap-2 mt-4">
                        {Object.entries(matchBreakdown).map(([key, data]) => (
                          <div key={key} className="text-center">
                            <p className="text-lg font-semibold">{data.score}%</p>
                            <p className="text-xs text-slate-500">{data.label}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Location & Basic Info */}
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="w-5 h-5 text-primary" />
                <span className="font-medium">{property.municipality}</span>
              </div>
              
              {/* Features Grid */}
              <FeaturesGrid features={property.features} />
              
              {/* Description / Story */}
              {property.story && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Historia de la Propiedad</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 leading-relaxed">{property.story}</p>
                  </CardContent>
                </Card>
              )}
              
              {/* Vibe Tags */}
              {property.vibe_tags && property.vibe_tags.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4" /> Vibe Tags
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {property.vibe_tags.map(tagId => {
                        const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                        return tag ? <VibeTag key={tagId} tag={tag} /> : null;
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Owner & Agent Info */}
              {(owner || agent) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {owner && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <User className="w-4 h-4" /> Propietario
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="font-medium">{owner.user?.name || owner.name || "N/A"}</p>
                        <p className="text-sm text-slate-500">{owner.user?.email || owner.email}</p>
                      </CardContent>
                    </Card>
                  )}
                  {agent && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Building className="w-4 h-4" /> Agente
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="font-medium">{agent.user?.name || agent.name || "N/A"}</p>
                        <p className="text-sm text-slate-500">{agent.user?.email || agent.email}</p>
                        {(agent.profile?.agency_name || agent.agency_name) && (
                          <Badge variant="outline" className="mt-2">{agent.profile?.agency_name || agent.agency_name}</Badge>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
              
              {/* Statistics */}
              {statistics && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Estadísticas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-blue-50 rounded-xl">
                        <p className="text-2xl font-bold text-blue-600">{statistics.views || 0}</p>
                        <p className="text-xs text-slate-500">Vistas</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-xl">
                        <p className="text-2xl font-bold text-purple-600">{statistics.total_swipes || 0}</p>
                        <p className="text-xs text-slate-500">Swipes</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-xl">
                        <p className="text-2xl font-bold text-green-600">{statistics.total_matches || 0}</p>
                        <p className="text-xs text-slate-500">Matches</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </ScrollArea>
        
        {/* Action Buttons */}
        {!isEditing && (canEdit || canDelete || canApprove) && (
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
            <div className="flex gap-2 w-full justify-between">
              <div className="flex gap-2">
                {canApprove && property.status === "pending_approval" && (
                  <>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={() => onApprove?.(property.id)}>
                      <Check className="w-4 h-4 mr-2" /> Aprobar
                    </Button>
                    <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => onReject?.(property.id)}>
                      <XCircle className="w-4 h-4 mr-2" /> Rechazar
                    </Button>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                {canEdit && (
                  <Button variant="outline" onClick={() => setIsEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" /> Editar
                  </Button>
                )}
                {canDelete && (
                  <Button variant="destructive" onClick={handleDelete}>
                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                  </Button>
                )}
              </div>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Property Card Component - Clickable card for listings
export const PropertyCard = ({ 
  property, 
  onClick, 
  showOwner = false, 
  showAgent = false,
  owner = null,
  agent = null,
  compact = false,
  showMatchScore = false,
  matchScore = null
}) => {
  return (
    <div 
      onClick={onClick}
      className={`flex items-center justify-between p-4 hover:bg-slate-50 rounded-xl transition-all cursor-pointer border border-transparent hover:border-slate-200 ${compact ? 'gap-3' : 'gap-4'}`}
      data-testid={`property-card-${property.id}`}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        {/* Property Image */}
        <div className={`${compact ? 'w-14 h-10' : 'w-20 h-14'} flex-shrink-0 rounded-lg overflow-hidden bg-slate-100`}>
          {property.images?.[0] ? (
            <img 
              src={property.images[0]} 
              alt={property.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Home className="w-5 h-5 text-slate-300" />
            </div>
          )}
        </div>
        
        {/* Property Info */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-800 truncate">{property.title}</p>
          <p className="text-sm text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3 flex-shrink-0" /> 
            <span className="truncate">{property.municipality}</span>
          </p>
          
          {/* Features summary */}
          {property.features && !compact && (
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
              {property.features.bedrooms !== undefined && (
                <span className="flex items-center gap-1">
                  <Bed className="w-3 h-3" /> {property.features.bedrooms}
                </span>
              )}
              {property.features.bathrooms !== undefined && (
                <span className="flex items-center gap-1">
                  <Bath className="w-3 h-3" /> {property.features.bathrooms}
                </span>
              )}
              {(property.features.land_area_m2 || property.features.area) && (
                <span className="flex items-center gap-1" title="Terreno">
                  <Square className="w-3 h-3" /> {property.features.land_area_m2 || property.features.area}m²
                </span>
              )}
              {property.features.construction_area_m2 && (
                <span className="flex items-center gap-1" title="Construido">
                  <Home className="w-3 h-3" /> {property.features.construction_area_m2}m²
                </span>
              )}
              {property.features.price !== undefined && (
                <span className="font-medium text-primary">
                  ${(property.features.price / 1000000).toFixed(1)}M
                </span>
              )}
            </div>
          )}
          
          {/* Owner/Agent info + Psychographic badge */}
          {!compact && (
            <div className="flex items-center gap-3 mt-1 text-xs flex-wrap">
              {showOwner && owner && (
                <span className="text-slate-400 flex items-center gap-1">
                  <User className="w-3 h-3" /> {owner.name || owner.user?.name || "N/A"}
                </span>
              )}
              {showAgent && agent && (
                <span className="text-purple-500 flex items-center gap-1">
                  <Building className="w-3 h-3" /> {agent.agency_name || agent.profile?.agency_name || agent.name || agent.user?.name || "N/A"}
                </span>
              )}
              {/* Psychographic completion badge */}
              <PsychographicBadge psychographicAttributes={property.psychographic_attributes} onClick={onClick} />
            </div>
          )}
        </div>
      </div>
      
      {/* Right side info */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Vibe Tags */}
        {property.vibe_tags && property.vibe_tags.length > 0 && !compact && (
          <div className="hidden md:flex gap-1">
            {property.vibe_tags.slice(0, 2).map(tagId => {
              const tag = getAllVibeTagsArray().find(t => t.id === tagId);
              return tag ? <VibeTag key={tagId} tag={tag} size="sm" /> : null;
            })}
            {property.vibe_tags.length > 2 && (
              <Badge variant="outline" className="text-xs">+{property.vibe_tags.length - 2}</Badge>
            )}
          </div>
        )}
        
        {/* Match Score */}
        {showMatchScore && matchScore !== null && (
          <Badge className="bg-primary/10 text-primary border-0 font-bold">
            {matchScore}%
          </Badge>
        )}
        
        {/* Status Badge */}
        <Badge 
          variant={property.status === "active" ? "default" : "secondary"}
          className={property.status === "active" ? "bg-green-100 text-green-700 hover:bg-green-100" : 
                     property.status === "pending_approval" ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : ""}
        >
          {property.status === "active" ? "Activa" : 
           property.status === "pending_approval" ? "Pendiente" : property.status}
        </Badge>
        
        {/* Stats */}
        {property.match_count !== undefined && (
          <div className="text-right hidden sm:block">
            <p className="font-semibold text-primary">{property.avg_match_score || 0}%</p>
            <p className="text-xs text-slate-500">{property.match_count || 0} matches</p>
          </div>
        )}
        
        <ChevronRight className="w-5 h-5 text-slate-300" />
      </div>
    </div>
  );
};

export default PropertyDetailModal;
